import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { ConfigService, events } from '@nuskin/ns-util';
import { AdrService, CurrencyService } from '@nuskin/ns-shop';
import { UserService } from '@nuskin/ns-account';
import { NsLoyalty } from '@nuskin/ns-loyalty';

import template from './ns-adp-loyalty.html';

export class NsAdpLoyalty extends PolymerElement {
    static get is() {
        return 'ns-adp-loyalty';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * Language used to find translations
             */
            language: {
                type: String
            },
            /**
             * Stores the selectedAdr for any child components to grab from
             */
            selectedAdr: {
                type: Object,
                notify: true,
                observer: 'observerSelectedAdr'
            },
            /**
             * Country used for service calls
             */
            country: {
                type: String
            },
            /**
             * isOptedIn
             * is Current User opted into loyalty
             */
            isOptedIn: {
                type: Boolean,
                value: false
            },
            /**
             * loyaltyConfig
             * Configuration for loyalty
             */
            loyaltyConfig: {
                type: Object
            },
            /**
             * loyaltyService
             * Service for loyalty
             */
            loyaltyService: {
                type: Object
            },
            /**
             * translations
             * Translations contributed by shop-ui content in aem author
             */
            translations: {
                type: Object,
                observer: 'observerTranslations'
            },
            /**
             * isOverRedemptionLimit
             * Is the current ADR/subscription currently redeeming points
             */
            isOverRedemptionLimit: {
                type: Boolean,
                value: false
            },
            /**
             * isEditing
             * Determine what part of the box we are showing if we are or aren't redeeming points for this adr
             */
            isEditing: {
                type: Boolean,
                value: false
            },
            /**
             * showEditButton
             * Determine if we can show the button to edit the amount to be redeemed
             */
            showEditButton: {
                type: Boolean,
                value: false
            },
            /**
             * loyaltyPointsAvailableAmount
             * Amount of loyaltyPoints available
             */
            loyaltyPointsAvailableAmount: {
                type: Number,
                value: 0
            },
            /**
             * amountToRedeem
             * Amount of points customer wants to redeem
             */
            amountToRedeem: {
                type: Number,
                observer: 'observerAmountToRedeem'
            },
            /**
             * maxRedemptionAmount
             * Maximum amount of points to be redeemed
             */
            maxRedemptionAmount: {
                type: Number
            },
            /**
             * originalAmount
             * Amount of points gotten originally for redemption
             */
            originalAmount: {
                type: Number
            },
            /**
             * computedLoyaltyPointsAvailableText
             * Text as a result of two values to show customer
             */
            computedLoyaltyPointsAvailableText: {
                type: String,
                computed: 'computeLoyaltyPointsText(loyaltyPointsAvailableAmount, loyaltyPointsAvailableText)'
            },
            /**
             * computedRedeemingPointsCurrency
             * Text as a result of two values to show customer
             */
            computedRedeemingPointsCurrency: {
                type: String,
                computed: 'computeRedeemingPointsCurrency(loyaltyRedeemingPointsMessage, amountToRedeem)'
            },
            /**
             * showLoader
             */
            showLoader: {
                type: Boolean,
                notify: true
            },
            /**
             * Collection of toast message objects, shared with the parent (ns-adp-wrapper).
             * Messages in this collection are displayed by the parent.
             */
            toastMessages: {
                type: Array,
                notify: true
            },
            /**
             * computedLoyaltyPointsAvailableText
             * Text as a result of two values to show customer
             */
            computedLoyaltyOverMaximumRedemptionAmount: {
                type: String,
                computed: 'computeLoyaltyOverMaximumRedemptionAmount(loyaltyOverMaximumRedemptionAmount, maxRedemptionAmount)'
            },
            /**
             * This section is for translation fallback
             */
            loyaltySectionTitle: {
                type: String,
                value: 'Enjoy Credits'
            },
            loyaltyRedeemButtonText: {
                type: String,
                value: 'Enjoy Credits'
            },
            loyaltyPointsAvailableText: {
                type: String,
                value: '{points} Credits Available'
            },
            loyaltySaveErrorMessage: {
                type: String,
                value: 'Error when saving Enjoy credits please try again later.'
            },
            loyaltyOverMaximumRedemptionAmount: {
                type: String,
                value: 'Cannot redeem that many points. Maximum Amount: {maxAmount}'
            },
            loyaltyNotEditingDescriptionText: {
                type: String,
                value: 'You can earn and redeem enJoy credits on your subscriptions orders'
            },
            loyaltyLearnMoreButtonText: {
                type: String,
                value: 'Learn more about enJoy Rewards'
            },
            loyaltyNotEditingExtraText: {
                type: String,
                value: 'enJoy earning available after order completion'
            },
            loyaltyRedeemInputLabel: {
                type: String,
                value: 'Credits To redeem on this order'
            },
            loyaltyRedeemingPointsMessage: {
                type: String,
                value: 'Your enJoy points will cover {redeemingPoints} of this order.'
            },
            loyaltySaveExtraText: {
                type: String,
                value: `*Note: if you don't have enough Enjoy credits in your wallet when your order is processed it will not be applied. Please allow up to 48 hours for your order to be processed before these credits are available.`
            },
            loyaltyCancelEditingButtonText: {
                type: String,
                value: 'Cancel'
            },
            loyaltySaveEditingButtonText: {
                type: String,
                value: 'Save'
            },
            loyaltyOverCurrentWalletAmount: {
                type: String,
                value: 'Amount of Loyalty credits applied exceeds available balance.'
            }
        };
    }

    ready() {
        super.ready();
        this.checkShowRedeemButton();
        this.setConfig();
    }

    async setConfig() {
        if (!this.loyaltyService) {
            this._setLoyaltyService();
        }
        this.loyaltyInfo = await this.loyaltyService.getRewardsBucketsAndCurrentTierData();

        // This is to trigger the observer
        this.observerSelectedAdr();

        this.set('isOptedIn', this.loyaltyInfo.optInStatus);
        if (this.isOptedIn) {
            this.checkShowRedeemButton();
            this.set('loyaltyPointsAvailableAmount', this.loyaltyInfo.wallet.available);
        }

    }

    computeLoyaltyPointsText(amount, text) {
        return text.replace('{points}', `<ns-currency class="bold-currency" amount="${amount}" country="[[country]]" language="[[language]]"></ns-currency>`);
    }

    computeRedeemingPointsCurrency(redeemingPointsMessage, amountToRedeem) {
        return redeemingPointsMessage.replace('{redeemingPoints}', `<ns-currency class="bold-currency" amount="${amountToRedeem}" country="[[country]]" language="[[language]]"></ns-currency>`);
    }

    computeLoyaltyOverMaximumRedemptionAmount(message, maxAmount) {
        return message.replace('{maxAmount}', maxAmount);
    }

    /**
     * Assigns our translations
     */
    observerTranslations(translations) {
        const propsToCheck = [
            'loyaltySectionTitle',
            'loyaltyRedeemButtonText',
            'loyaltyPointsAvailableText',
            'loyaltySaveErrorMessage',
            'loyaltyOverMaximumRedemptionAmount',
            'loyaltyOverCurrentWalletAmount',
            'loyaltyNotEditingDescriptionText',
            'loyaltyLearnMoreButtonText',
            'loyaltyNotEditingExtraText',
            'loyaltyRedeemInputLabel',
            'loyaltyRedeemingPointsMessage',
            'loyaltySaveExtraText',
            'loyaltyCancelEditingButtonText',
            'loyaltySaveEditingButtonText'
        ];

        propsToCheck.forEach(propName => {
            let translation = translations[propName];
            if (translation) this.set(propName, translation);
        });
    }

    /**
     * Monitors for a change to the selected ADR
     */
     async observerSelectedAdr() {
        if(this.selectedAdr) {
            this.originalAmount = this.selectedAdr.pointsUsed;
            this.amountToRedeem = this.selectedAdr.pointsUsed;
            if (!this.loyaltyService) {
                this._setLoyaltyService();
            }

            if (this.loyaltyService.currentTierData) {
                let computedTotal = await AdrService.getAdrLoyaltyRedemptionMax(this.selectedAdr['key']);
                let limit = computedTotal * this.loyaltyService.currentTierData.redemptionLimit;
                this.set('maxRedemptionAmount', parseFloat(limit.toFixed(2)));
            }
            this.swapEditing(false);
        }
    }

    /**
     * Changes the state of the loyalty box.
     * @param {boolean} [isEditing] - force state by passing this parameter
     */
    swapEditing(isEditing) {
        if (isEditing !== undefined) this.set('isEditing', isEditing);
        else this.set('isEditing', !this.isEditing);

        this.checkShowRedeemButton();
    }

    async onSave() {
        if (this.amountToRedeem > this.maxRedemptionAmount) {
            this._addToastMessage('error', {
                header: this.computedLoyaltyOverMaximumRedemptionAmount,
                scrollIntoView: true
            });
        } else if (this.amountToRedeem > this.loyaltyPointsAvailableAmount) {
            this._addToastMessage('error', {
                header: this.loyaltyOverCurrentWalletAmount,
                scrollIntoView: true
            });
        }else {
            this.set('showLoader', true);
            let currencyCode = CurrencyService.getCurrency(this.country).currencyCode;
            const amountToRedeem = CurrencyService.parseCurrencyStringToFloat(this.amountToRedeem)
            try {
                let result = await AdrService.editAdrLoyaltyRedemption(this.selectedAdr['key'], amountToRedeem, currencyCode);
                events.publish(events.subscriptions.SUBSCRIPTION_LOYALTY_POINTS_UPDATED, result);
            } catch (e) {
                if (e && e.messages && e.messages.some(message => {
                    return message.code == "loyaltyRedemptionLimit"
                })) {
                    try {
                        await AdrService.editAdrLoyaltyRedemption(this.selectedAdr['key'],0, currencyCode);
                        let result2 = await AdrService.editAdrLoyaltyRedemption(this.selectedAdr['key'], amountToRedeem, currencyCode);
                        events.publish(events.subscriptions.SUBSCRIPTION_LOYALTY_POINTS_UPDATED, result2);
                    } catch(err) {
                        this._handleLoyaltySaveError(err)
                    }
                } else {
                    this._handleLoyaltySaveError(e)
                }
                
            } finally {
                this.swapEditing(false);
                this.set('showLoader', false);
            }
        }
    }

    _handleLoyaltySaveError(e) {
        this.amountToRedeem = this.originalAmount;
        console.error(e);
        this._addToastMessage('error', {
            header: this.loyaltySaveErrorMessage,
            scrollIntoView: true
        });
    }

    _setLoyaltyService() {
        const user = UserService.getUser();
        const marketConfig = ConfigService.getMarketConfig();

        let configuration = {
            requestUrl: marketConfig.awsUrl,
            market: user.countryCd,
            userId: user.id,
            jwt: 'JWT ' + user.eid,
            client_id: marketConfig.checkout.clientId,
            client_secret: marketConfig.checkout.clientSecret
        };
        this.loyaltyService = new NsLoyalty(configuration);
    }

    onCancel() {
        this.amountToRedeem = this.originalAmount;
        this.swapEditing();
    }

    /**
     * Determine if we can show the redeem button to the user.
     */
    checkShowRedeemButton() {
        let showEditButton = !this.isEditing && this.isOptedIn;
        this.set('showEditButton', showEditButton);
    }

    /**
     * Adds a toast message for display to the user.
     * @param {string} type - message type: "success", "warning", "error", "information"
     * @param {Object} options - options:
     * @param {string} [options.header] - message header text
     * @param {string} [options.message] - message body text
     * @param {boolean} [options.scrollIntoView = false] - flag indicating whether to scroll to the toast message
     * @param {boolean} [options.showPrintOption = false] - flag indicating whether to show a print button
     */
    _addToastMessage(type, options = {}) {
        const MESSAGE_DEFAULTS = {
            type: 'information',
            header: '',
            scrollIntoView: false,
            showPrintOption: false
        };
        const messageObject = Object.assign({}, MESSAGE_DEFAULTS, options, { type });
        this.push('toastMessages', messageObject);
    }
}

customElements.define(NsAdpLoyalty.is, NsAdpLoyalty);
